import React, { useState, useEffect, useRef } from 'react';
import AppIcon from '../common/AppIcon';
import GradientIcon from '../common/GradientIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import { saveGuestResult, hasClaimableGuestResult } from '../../utils/guestSession';
import GuestSignupPrompt from './GuestSignupPrompt';
import PointsEarnedRow from './PointsEarnedRow';

// 🟢 [访客模式] guest 默认 false —— 已有的 /games/word-scramble 路由行为完全不变
const WordScrambleGame = ({ guest = false }) => {
  const navigate = useNavigate();
  const { user, reloadUser } = useAuth();

  const [gameState, setGameState] = useState('menu');
  const [currentWord, setCurrentWord] = useState(null);
  const [scrambledLetters, setScrambledLetters] = useState([]);
  const [selectedLetters, setSelectedLetters] = useState([]);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [timeLeft, setTimeLeft] = useState(120);
  const [feedback, setFeedback] = useState('');
  const [wordsCompleted, setWordsCompleted] = useState(0);
  const [attempts, setAttempts] = useState(3);

  const [vocabularyPool, setVocabularyPool] = useState([]);

  // 🟢 [修复] 结算画面此前显示的是客户端原始分（"Points Earned: {score}"），
  // 而后端 routes/games.js 会把积分 clamp 到 MAX_GAME_POINTS=500，还可能加每日任务奖励，
  // 并把真实入账值放在响应的 pointsEarned 里 —— 三个游戏都拿到了却丢掉了。
  // 现在显示后端返回的真实值：null = 还没结算/没入账，不谎报数字。
  const [pointsEarned, setPointsEarned] = useState(null);


  // 🟢 [修复] 用 ref 保存最新分数/完成数，避免 endGame 从过期闭包读取，漏算最后一秒的得分
  const scoreRef = useRef(0);
  const wordsCompletedRef = useRef(0);
  const endGameCalledRef = useRef(false);
  useEffect(() => { scoreRef.current = score; }, [score]);
  useEffect(() => { wordsCompletedRef.current = wordsCompleted; }, [wordsCompleted]);

  useEffect(() => {
    const fetchWords = async () => {
      try {
        const res = guest
          ? await apiService.getPublicRandomVocabulary('all', 30)   // 🟢 公开、缓存、封顶
          : await apiService.getRandomVocabulary('all', 30);
        if (res.vocabulary) setVocabularyPool(res.vocabulary);
      } catch (error) {
        console.error("Failed to fetch words:", error);
      }
    };
    fetchWords();
    // 🟢 卸载时打断发音，避免离开页面后仍在朗读
    return () => { if ('speechSynthesis' in window) window.speechSynthesis.cancel(); };
  }, [guest]);

  const scrambleLetters = (word) => {
    return word.split('').map((letter, index) => ({
      id: index,
      letter: letter.toUpperCase(),
      isSelected: false
    })).sort(() => Math.random() - 0.5);
  };

  const generateNewWord = () => {
    const sourceList = vocabularyPool.length > 0 ? vocabularyPool : [];
    if (sourceList.length === 0) return;

    const randomWord = sourceList[Math.floor(Math.random() * sourceList.length)];
    setCurrentWord(randomWord);
    setScrambledLetters(scrambleLetters(randomWord.word));
    setSelectedLetters([]);
    setFeedback('');
    setAttempts(3);
  };

  const startGame = () => {
    // 🟢 修改：不弹窗，直接返回
    if (vocabularyPool.length === 0) return;

    endGameCalledRef.current = false; // 🟢 解锁本局结算
    setGameState('playing');
    setScore(0);
    setStreak(0);
    setPointsEarned(null);            // 🟢 新的一局，清掉上一局的入账结果
    setTimeLeft(120);
    setWordsCompleted(0);
    generateNewWord();
  };

  const handleLetterClick = (letterId) => {
    const letter = scrambledLetters.find(l => l.id === letterId);
    if (letter && !letter.isSelected) {
      setSelectedLetters(prev => [...prev, letter]);
      setScrambledLetters(prev =>
        prev.map(l => l.id === letterId ? { ...l, isSelected: true } : l)
      );
    }
  };

  const clearSelection = () => {
    setSelectedLetters([]);
    setScrambledLetters(prev =>
      prev.map(l => ({ ...l, isSelected: false }))
    );
  };

  const checkWord = () => {
    const builtWord = selectedLetters.map(l => l.letter).join('').toLowerCase();
    const correctWord = currentWord.word.toLowerCase();

    if (builtWord === correctWord) {
      const points = 10 + (streak * 2);
      setScore(prev => prev + points);
      setStreak(prev => prev + 1);
      setWordsCompleted(prev => prev + 1);
      setFeedback('Correct! 🎉');

      setTimeout(() => {
        generateNewWord();
      }, 1500);
    } else {
      setAttempts(prev => prev - 1);
      setFeedback(`Try again! ${attempts - 1} attempts left`);

      if (attempts <= 1) {
        setStreak(0);
        setFeedback(`The word was "${currentWord.word}"`);
        setTimeout(() => {
          generateNewWord();
        }, 2000);
      } else {
        clearSelection();
      }
    }
  };

  const endGame = async () => {
    // 🟢 幂等：本局已结算过就返回，避免重复 saveGameScore
    if (endGameCalledRef.current) return;
    endGameCalledRef.current = true;

    setGameState('gameOver');
    // 🟢 从 ref 读取真实分数，含最后一秒的得分
    const finalScore = scoreRef.current;
    const finalWords = wordsCompletedRef.current;

    // 🟢 [访客模式] 不调用任何需要 Token 的接口（saveGameScore / reloadUser /
    // checkBadges 都会被 authMiddleware 401 掉），只把这一局留在本 tab。
    if (guest) {
      saveGuestResult({
        gameType: 'word-scramble',
        score: finalScore,
        accuracy: 0,
        wordsCorrect: finalWords,
        wordsTotal: finalWords,
        timeSpent: 120,
        displayScore: finalScore,
        playedAt: Date.now()
      });
      return;
    }

    try {
      if (finalScore > 0) {
        const saveRes = await apiService.saveGameScore({
          gameType: 'word-scramble',
          score: finalScore,
          duration: 120,
          wordsCompleted: finalWords
        });

        // 🟢 [修复] 用后端真实入账的积分，而不是客户端原始分
        setPointsEarned(saveRes?.pointsEarned ?? null);

        // 刷新用户信息 (更新积分显示)
        await reloadUser();

        // 🟢 [修复] 触发 'game_played' 徽章：用后端事务返回的累计场次
        const totalGames = saveRes?.totalGamesPlayed || 0;
        if (totalGames > 0) await apiService.checkBadges('game_played', totalGames);
      }
    } catch (error) {
      console.error("Save score error:", error);
      // 🟢 [修复] 只在还没拿到入账结果时才标记失败 —— 这个 catch 也会捕获
      // checkBadges 的报错，那时积分已经入账，不能把真实数字覆盖掉
      setPointsEarned(prev => (typeof prev === 'number' ? prev : 'failed'));
    }
  };

  // 🟢 [修复] 单个计时器只依赖 gameState，用函数式更新，不再每秒重建 interval。
  useEffect(() => {
    if (gameState !== 'playing') return;
    const timer = setInterval(() => {
      setTimeLeft(prev => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [gameState]);

  // 时间归零 → 结束 (在更新函数外触发，配合幂等锁只结算一次)
  useEffect(() => {
    if (gameState === 'playing' && timeLeft <= 0) {
      endGame();
    }
  }, [timeLeft, gameState]);

  const speakWord = (word) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(word);
      utterance.rate = 0.85;
      utterance.pitch = 1.25;
      utterance.volume = 1;
      utterance.text = word;
      const voices = speechSynthesis.getVoices();
      const friendlyVoice = voices.find(voice =>
        voice.name.toLowerCase().includes('susan') ||
        voice.name.toLowerCase().includes('samantha') ||
        (voice.lang.includes('en-GB') && voice.name.toLowerCase().includes('female'))
      );
      if (friendlyVoice) utterance.voice = friendlyVoice;
      speechSynthesis.speak(utterance);
    }
  };

  const renderMenu = () => {
    // 🟢 计算加载状态
    const isLoading = vocabularyPool.length === 0;

    return (
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center max-w-2xl mx-auto"
      >
        <h1 className="text-4xl sm:text-6xl font-bold text-gray-800 mb-8 font-kid">
          🔤 WORD SCRAMBLE
        </h1>
        <p className="text-xl text-gray-800 mb-8">
          Unscramble the letters to form vocabulary words!
        </p>

        <div className="bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">How to Play:</h2>
          <div className="text-gray-800 space-y-2 text-left">
            <p>• Look at the scrambled letters</p>
            <p>• Read the word definition carefully</p>
            <p>• Click letters to build the correct word</p>
            <p>• Use the definition clue to help you</p>
            <p>• Build streaks for bonus points!</p>
          </div>
        </div>

        {/* 🟢 修改后的按钮：带有加载状态 */}
        <motion.button
          whileHover={{ scale: isLoading ? 1 : 1.05 }}
          whileTap={{ scale: isLoading ? 1 : 0.95 }}
          onClick={startGame}
          disabled={isLoading}
          className={`
            font-bold py-4 px-8 rounded-full text-xl shadow-lg transition-all
            ${isLoading
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-white text-purple-600 hover:bg-gray-100 cursor-pointer'}
          `}
        >
          {isLoading ? (
            <span className="flex items-center justify-center">
              <AppIcon name="spinner" className="mr-3" /> Loading Words...
            </span>
          ) : (
            'Start Scrambling!'
          )}
        </motion.button>
      </motion.div>
    );
  };

  const renderGame = () => (
    <div className="max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-8 text-gray-800">
        <div className="bg-white bg-opacity-80 backdrop-blur-lg rounded-2xl p-4 shadow-lg border-2 border-pink-200">
          <div className="text-2xl font-bold text-purple-600">Score: {score}</div>
          <div className="text-purple-500">Streak: {streak}</div>
        </div>
        <div className="bg-white bg-opacity-80 backdrop-blur-lg rounded-2xl p-4 text-center shadow-lg border-2 border-pink-200">
          <div className="text-3xl font-bold text-blue-600">{timeLeft}</div>
          <div className="text-sm text-blue-500">seconds left</div>
        </div>
        <div className="bg-white bg-opacity-80 backdrop-blur-lg rounded-2xl p-4 shadow-lg border-2 border-pink-200">
          <div className="text-2xl font-bold text-green-600">{wordsCompleted}</div>
          <div className="text-green-500">words completed</div>
        </div>
      </div>
      {currentWord && (
        <motion.div key={currentWord.id} initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-3xl p-8 shadow-2xl border-4 border-pink-300" style={{ backgroundColor: '#f8f9ff' }}>
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Unscramble the letters to make a word!</h2>
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => speakWord(currentWord.word)} className="bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-full shadow-lg mb-6"><AppIcon name="volume-up" className="mr-2" />Listen to Word</motion.button>
          </div>
          <div className="text-center mb-8">
            <div className="min-h-[80px] bg-blue-50 rounded-2xl p-4 mb-6 border-2 border-blue-200">
              <div className="flex justify-center items-center space-x-2 flex-wrap">
                {selectedLetters.length > 0 ? (selectedLetters.map((letter, index) => (<motion.div key={`selected-${index}`} initial={{ scale: 0 }} animate={{ scale: 1 }} className="bg-yellow-400 text-black font-bold text-2xl w-12 h-12 rounded-lg flex items-center justify-center shadow-lg">{letter.letter}</motion.div>))) : (<div className="text-2xl font-bold text-blue-600">Start building...</div>)}
              </div>
            </div>
          </div>
          <div className="flex justify-center mb-8">
            <div className="flex flex-wrap justify-center gap-3 max-w-md">
              {scrambledLetters.map((letter) => (<motion.button key={letter.id} whileHover={{ scale: letter.isSelected ? 1 : 1.1 }} whileTap={{ scale: letter.isSelected ? 1 : 0.9 }} onClick={() => handleLetterClick(letter.id)} disabled={letter.isSelected} className={`w-16 h-16 rounded-lg font-bold text-2xl shadow-lg transition-all duration-200 ${letter.isSelected ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-yellow-400 text-black hover:bg-yellow-500 cursor-pointer'}`}>{letter.letter}</motion.button>))}
            </div>
          </div>
          <div className="bg-gradient-to-br from-purple-100 to-purple-200 rounded-2xl p-6 mb-6 border-2 border-purple-300">
            <h3 className="text-xl font-bold text-purple-800 mb-3 text-center">💡 Word Definition</h3>
            <div className="text-lg text-purple-700 text-center leading-relaxed">{currentWord.definition}</div>
            <div className="text-sm text-purple-600 text-center mt-2 italic">Find the word that matches this definition!</div>
          </div>
          <div className="flex justify-center space-x-4 mb-6">
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={clearSelection} className="bg-red-500 hover:bg-red-600 text-white font-bold py-3 px-6 rounded-full shadow-lg">Clear</motion.button>
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={checkWord} disabled={selectedLetters.length === 0} className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white font-bold py-3 px-6 rounded-full shadow-lg disabled:cursor-not-allowed">Check Word</motion.button>
          </div>
          <AnimatePresence>
            {feedback && (<motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className={`text-center text-xl font-bold p-4 rounded-lg ${feedback.includes('Correct') ? 'bg-green-100 text-green-800 border-2 border-green-300' : 'bg-red-100 text-red-800 border-2 border-red-300'}`}><div className="text-3xl mb-2">{feedback.includes('Correct') ? '🎉' : '❌'}</div><div className="text-xl font-bold mb-2">{feedback}</div></motion.div>)}
          </AnimatePresence>
        </motion.div>
      )}
    </div>
  );

  const renderGameOver = () => (
    <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} className="text-center max-w-2xl mx-auto">
      <h1 className="text-3xl sm:text-5xl font-bold text-gray-800 mb-8">{score > 100 ? '🏆 Excellent Scrambling!' : '⏰ Time\'s Up!'}</h1>
      <div className="bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl p-8 mb-8">
        <div className="text-gray-800 space-y-4">
          <div className="text-4xl font-bold">Final Score: {score}</div>
          <div className="text-xl">Words Completed: {wordsCompleted}</div>
          <div className="text-xl">Best Streak: {streak}</div>
          {/* 🟢 访客不显示积分行 —— 注册前无从得知后端会给多少，由注册弹窗负责说明 */}
          {!guest && <PointsEarnedRow score={score} pointsEarned={pointsEarned} />}
        </div>
      </div>
      <div className="space-x-4">
        <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={startGame} className="bg-white text-purple-600 hover:bg-gray-100 font-bold py-3 px-6 rounded-full">Play Again</motion.button>
        <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => navigate(guest ? '/try' : '/games')} className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 rounded-full">{guest ? 'More Games' : 'Back to Games'}</motion.button>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-100 via-sky-200 to-blue-300 p-6">
      <AnimatePresence mode="wait">
        {gameState === 'menu' && renderMenu()}
        {gameState === 'playing' && renderGame()}
        {gameState === 'gameOver' && renderGameOver()}
      </AnimatePresence>

      {/* 🟢 [访客模式] 结算后叠加快速注册引导；
          F5 之后游戏回到菜单页，但成绩还在 sessionStorage 里 ——
          这时以细横幅形态出现，保证「随时可以领」不是空话。
          登录用户完全不渲染。 */}
      {guest && (gameState === 'gameOver' || (gameState === 'menu' && hasClaimableGuestResult())) && (
        <GuestSignupPrompt onPlayAgain={startGame} bannerOnly={gameState !== 'gameOver'} />
      )}
    </div>
  );
};

export default WordScrambleGame;